import React, { useRef, useEffect, useCallback } from "react";
import { Bold, Italic, Underline, Link as LinkIcon, List, ListOrdered, Heading1, Heading2, Undo, Redo } from "lucide-react";

/**
 * Minimal contenteditable-based WYSIWYG. No 3rd-party dependency — uses the
 * legacy but universally supported document.execCommand for a bounded set of
 * inline formats plus lists and links. Emits raw HTML through `onChange`.
 *
 * Deliberately not exposed anywhere users can paste from Word / Google Docs
 * unsanitized — this component is only used by superadmin composers. If we
 * broaden its use, add DOMPurify on save.
 */
export default function RichTextEditor({ value, onChange, placeholder = "Write your message…", testId = "wysiwyg-editor" }) {
    const ref = useRef(null);
    const lastEmitted = useRef("");

    // Sync incoming `value` into the DOM only when it diverges from what we
    // last emitted, so we never fight the user's caret while typing.
    useEffect(() => {
        if (!ref.current) return;
        if (value !== lastEmitted.current && ref.current.innerHTML !== value) {
            ref.current.innerHTML = value || "";
        }
    }, [value]);

    const exec = useCallback((cmd, arg) => {
        // execCommand is deprecated but still widely supported and is by far
        // the smallest way to ship a bounded WYSIWYG.
        document.execCommand(cmd, false, arg);
        ref.current?.focus();
        const html = ref.current?.innerHTML || "";
        lastEmitted.current = html;
        onChange?.(html);
    }, [onChange]);

    const onInput = (e) => {
        const html = e.currentTarget.innerHTML;
        lastEmitted.current = html;
        onChange?.(html);
    };

    const insertLink = () => {
        const url = window.prompt("Link URL (https://…)");
        if (!url) return;
        exec("createLink", url);
    };

    const btn = "inline-flex items-center justify-center h-8 w-8 rounded-md text-ocean-700 hover:bg-sand-100 border border-transparent hover:border-sand-300 transition-colors";

    return (
        <div className="rounded-2xl border border-sand-300 bg-white overflow-hidden" data-testid={testId}>
            <div className="flex flex-wrap gap-1 border-b border-sand-200 bg-sand-50/50 px-2 py-1.5">
                <button type="button" className={btn} title="Bold" onClick={() => exec("bold")} data-testid={`${testId}-bold`}><Bold className="h-4 w-4" /></button>
                <button type="button" className={btn} title="Italic" onClick={() => exec("italic")} data-testid={`${testId}-italic`}><Italic className="h-4 w-4" /></button>
                <button type="button" className={btn} title="Underline" onClick={() => exec("underline")} data-testid={`${testId}-underline`}><Underline className="h-4 w-4" /></button>
                <span className="w-px bg-sand-300 mx-1 self-stretch" />
                <button type="button" className={btn} title="Heading 1" onClick={() => exec("formatBlock", "H1")}><Heading1 className="h-4 w-4" /></button>
                <button type="button" className={btn} title="Heading 2" onClick={() => exec("formatBlock", "H2")}><Heading2 className="h-4 w-4" /></button>
                <span className="w-px bg-sand-300 mx-1 self-stretch" />
                <button type="button" className={btn} title="Bulleted list" onClick={() => exec("insertUnorderedList")}><List className="h-4 w-4" /></button>
                <button type="button" className={btn} title="Numbered list" onClick={() => exec("insertOrderedList")}><ListOrdered className="h-4 w-4" /></button>
                <button type="button" className={btn} title="Insert link" onClick={insertLink} data-testid={`${testId}-link`}><LinkIcon className="h-4 w-4" /></button>
                <span className="w-px bg-sand-300 mx-1 self-stretch" />
                <button type="button" className={btn} title="Undo" onClick={() => exec("undo")}><Undo className="h-4 w-4" /></button>
                <button type="button" className={btn} title="Redo" onClick={() => exec("redo")}><Redo className="h-4 w-4" /></button>
            </div>
            <div
                ref={ref}
                contentEditable
                suppressContentEditableWarning
                onInput={onInput}
                className="min-h-[220px] px-4 py-3 text-sm text-ocean-700 focus:outline-none prose prose-sm max-w-none"
                data-testid={`${testId}-area`}
                data-placeholder={placeholder}
                style={{ whiteSpace: "pre-wrap" }}
            />
            <style>{`[data-testid="${testId}-area"]:empty::before{content:attr(data-placeholder);color:#8b9aab;pointer-events:none;}`}</style>
        </div>
    );
}
